import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@pooflabs/web';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { useMobile } from '@/hooks/use-mobile';
import { Address, Time, transformValues, set } from '@/lib/db-client';
import {
  formatDuration,
  formatUnlockDate,
  getCommitmentLevel,
} from '@/utils/lockHelpers';
import { Lock, Calendar, AlertCircle, Zap, Shield, Skull, AlertTriangle } from 'lucide-react';
import {
  SUPPORTED_TOKENS,
  SOL,
  BONK,
  SKR,
  MEW,
  USDC,
  ANGELIC_SOL,
  GMT,
  DRAGON,
  getTokenByMint,
  parseTokenAmountBigInt,
  formatTokenAmount,
} from '@/utils/tokenConfig';
import { useTokenBalances } from '@/hooks/use-token-balances';

const TOKEN_OPTIONS = [SOL, BONK, SKR, MEW, USDC, ANGELIC_SOL, GMT, DRAGON] as const;

// Minimum SOL balance required to cover Solana network fees + potential token-account rent.
// 0.01 SOL is a safe buffer that covers typical transaction fees (~0.000005 SOL) plus
// token-account creation rent (~0.002 SOL) with headroom for network spikes.
const MIN_SOL_FOR_FEES = 0.01;

// ────────────────────────────────────────────────────────────────────
// Token selector
// ────────────────────────────────────────────────────────────────────
const TokenSelector: React.FC<{
  selectedMint: string;
  onChange: (mint: string) => void;
}> = ({ selectedMint, onChange }) => (
  <div
    className="grid grid-cols-3 sm:grid-cols-6 rounded-lg p-1 gap-1"
    style={{ background: 'hsl(30 5% 8%)', border: '1px solid hsl(35 12% 14%)' }}
  >
    {TOKEN_OPTIONS.map((mint) => {
      const token = SUPPORTED_TOKENS[mint];
      const isSelected = selectedMint === mint;
      return (
        <button
          key={mint}
          onClick={() => onChange(mint)}
          className="flex items-center justify-center gap-1 py-2 px-1.5 rounded-md text-xs font-semibold tracking-wide transition-all duration-200"
          style={
            isSelected
              ? {
                  background: `${token.brandColorBg}`,
                  border: `1px solid ${token.brandColorBorder}`,
                  color: token.brandColor,
                }
              : {
                  background: 'transparent',
                  border: '1px solid transparent',
                  color: 'hsl(35 8% 40%)',
                }
          }
        >
          {token.symbol}
        </button>
      );
    })}
  </div>
);

const PRESETS = [
  { label: '1 week', days: 7 },
  { label: '1 month', days: 30 },
  { label: '3 months', days: 90 },
  { label: '6 months', days: 180 },
  { label: '1 year', days: 365 },
];

const HOLD_DURATION_MS = 10_000;

interface CreateLockModalProps {
  open: boolean;
  onClose: () => void;
}

// ────────────────────────────────────────────────────────────────────
// Lock type toggle
// ────────────────────────────────────────────────────────────────────
const LockTypeToggle: React.FC<{
  lockType: 'standard' | 'perma';
  onChange: (t: 'standard' | 'perma') => void;
}> = ({ lockType, onChange }) => (
  <div
    className="flex rounded-lg p-1 gap-1"
    style={{ background: 'hsl(30 5% 8%)', border: '1px solid hsl(35 12% 14%)' }}
  >
    <button
      onClick={() => onChange('standard')}
      className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all duration-200"
      style={
        lockType === 'standard'
          ? {
              background: 'hsl(38 65% 57% / 0.18)',
              border: '1px solid hsl(38 65% 57% / 0.4)',
              color: 'hsl(43 75% 68%)',
            }
          : {
              background: 'transparent',
              border: '1px solid transparent',
              color: 'hsl(35 8% 45%)',
            }
      }
    >
      <Shield size={14} />
      Standard
    </button>
    <button
      onClick={() => onChange('perma')}
      className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all duration-200"
      style={
        lockType === 'perma'
          ? {
              background: 'hsl(0 70% 45% / 0.2)',
              border: '1px solid hsl(0 70% 50% / 0.5)',
              color: 'hsl(0 72% 68%)',
            }
          : {
              background: 'transparent',
              border: '1px solid transparent',
              color: 'hsl(35 8% 45%)',
            }
      }
    >
      <Skull size={14} />
      PermaLOCK
    </button>
  </div>
);

// ────────────────────────────────────────────────────────────────────
// Press-and-hold confirmation button
// ────────────────────────────────────────────────────────────────────
interface HoldButtonProps {
  disabled: boolean;
  onConfirm: () => void;
  loading: boolean;
}

const HoldButton: React.FC<HoldButtonProps> = ({ disabled, onConfirm, loading }) => {
  const [holding, setHolding] = useState(false);
  const [progress, setProgress] = useState(0); // 0–100
  const [released, setReleased] = useState(false);
  const startRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const firedRef = useRef(false);
  const holdingRef = useRef(false);

  const stopInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startHold = useCallback(() => {
    if (disabled || loading) return;
    firedRef.current = false;
    holdingRef.current = true;
    setReleased(false);
    setHolding(true);
    startRef.current = Date.now();

    intervalRef.current = setInterval(() => {
      if (startRef.current === null) return;
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min((elapsed / HOLD_DURATION_MS) * 100, 100);
      setProgress(pct);

      if (pct >= 100 && !firedRef.current) {
        firedRef.current = true;
        stopInterval();
        holdingRef.current = false;
        setHolding(false);
        onConfirm();
      }
    }, 50);
  }, [disabled, loading, onConfirm, stopInterval]);

  const endHold = useCallback(() => {
    if (!holdingRef.current) return;
    stopInterval();
    holdingRef.current = false;
    startRef.current = null;
    setHolding(false);
    if (!firedRef.current) {
      setReleased(true);
      setProgress(0);
    }
  }, [stopInterval]);

  const cancelHold = useCallback(() => {
    if (!holdingRef.current) return;
    stopInterval();
    holdingRef.current = false;
    startRef.current = null;
    setHolding(false);
    // pointercancel is an OS/browser interruption — do NOT treat as a completed hold,
    // and do NOT show "released too early" since the user didn't voluntarily release.
    if (!firedRef.current) {
      setProgress(0);
    }
  }, [stopInterval]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (holdingRef.current) return; // guard against double-fire
    e.currentTarget.setPointerCapture(e.pointerId);
    startHold();
  }, [startHold]);

  useEffect(() => {
    return stopInterval;
  }, [stopInterval]);

  // Visual glow stage based on progress
  const glowColor =
    progress < 30
      ? 'hsl(45 90% 50% / 0.45)'
      : progress < 70
      ? 'hsl(25 90% 50% / 0.55)'
      : 'hsl(0 80% 50% / 0.75)';

  const btnBg =
    progress < 30
      ? 'hsl(0 60% 22%)'
      : progress < 70
      ? 'hsl(20 70% 20%)'
      : 'hsl(0 75% 18%)';

  const borderColor =
    progress < 30
      ? 'hsl(45 80% 48%)'
      : progress < 70
      ? 'hsl(20 80% 50%)'
      : 'hsl(0 75% 52%)';

  const labelColor =
    progress < 30
      ? 'hsl(45 90% 70%)'
      : progress < 70
      ? 'hsl(20 90% 68%)'
      : 'hsl(0 80% 70%)';

  // Circumference for SVG ring
  const R = 11;
  const circ = 2 * Math.PI * R;
  const dash = circ * (progress / 100);

  return (
    <div className="space-y-2">
      <button
        onPointerDown={handlePointerDown}
        onPointerUp={endHold}
        onPointerLeave={endHold}
        onPointerCancel={cancelHold}
        disabled={disabled || loading}
        className="relative w-full py-5 rounded-lg font-bold tracking-widest uppercase text-sm overflow-hidden select-none transition-all duration-150"
        style={{
          background: disabled ? 'hsl(30 5% 12%)' : btnBg,
          border: `2px solid ${disabled ? 'hsl(35 12% 18%)' : borderColor}`,
          color: disabled ? 'hsl(35 8% 38%)' : labelColor,
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxShadow: holding && !disabled ? `0 0 28px ${glowColor}, 0 0 8px ${glowColor}` : 'none',
          transition: 'box-shadow 0.1s, border-color 0.15s, background 0.15s',
          touchAction: 'none',
        }}
      >
        {/* Fill sweep overlay */}
        {holding && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(90deg, hsl(0 70% 40% / 0.18) 0%, transparent 100%)`,
              width: `${progress}%`,
              transition: 'width 0.05s linear',
            }}
          />
        )}

        <span className="relative flex items-center justify-center gap-3">
          {/* SVG ring progress */}
          <svg width="28" height="28" viewBox="0 0 28 28" className="flex-shrink-0">
            {/* Track */}
            <circle
              cx="14" cy="14" r={R}
              fill="none"
              stroke="hsl(35 12% 18%)"
              strokeWidth="2.5"
            />
            {/* Progress arc */}
            <circle
              cx="14" cy="14" r={R}
              fill="none"
              stroke={progress < 30 ? 'hsl(45 90% 60%)' : progress < 70 ? 'hsl(20 90% 60%)' : 'hsl(0 80% 62%)'}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={circ / 4}
              style={{ transition: 'stroke-dasharray 0.05s linear' }}
            />
            {/* Icon in center */}
            <foreignObject x="7" y="7" width="14" height="14">
              <div className="flex items-center justify-center w-full h-full">
                {loading ? (
                  <Zap size={10} className="animate-spin" />
                ) : (
                  <Skull size={10} />
                )}
              </div>
            </foreignObject>
          </svg>

          {loading ? (
            'Locking...'
          ) : holding ? (
            `Hold... ${(((100 - progress) / 100) * (HOLD_DURATION_MS / 1000)).toFixed(1)}s`
          ) : (
            'Hold to Confirm PermaLOCK'
          )}
        </span>
      </button>

      {released && (
        <p
          className="text-xs text-center animate-fade-in"
          style={{ color: 'hsl(0 65% 60%)' }}
        >
          You released too early. Hold for the full 10 seconds to confirm.
        </p>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Main modal
// ────────────────────────────────────────────────────────────────────
export const CreateLockModal: React.FC<CreateLockModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const isMobile = useMobile();
  const [tokenMint, setTokenMint] = useState<string>(SOL);
  const [amountStr, setAmountStr] = useState('');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [lockType, setLockType] = useState<'standard' | 'perma'>('standard');

  // Fetch live balances for all tokens
  const { balances: tokenBalances } = useTokenBalances(user?.address);

  const token = getTokenByMint(tokenMint);
  const amountNum = parseFloat(amountStr) || 0;
  // Use BigInt string-based conversion to avoid floating-point precision loss.
  // depAmtBig and lkAmtBig stay as BigInt throughout to match the policy's exact integer math.
  // parseTokenAmountBigInt is guaranteed never to throw — it returns 0n for any invalid input,
  // including scientific-notation strings that mobile browsers/Phantom deep-link returns can produce.
  let depAmtBig: bigint = 0n;
  try {
    depAmtBig = amountNum > 0 ? parseTokenAmountBigInt(amountStr || '0', tokenMint) : 0n;
  } catch {
    depAmtBig = 0n;
  }
  const lkAmtBig: bigint = depAmtBig * 99n / 100n; // integer floor division — mirrors policy exactly
  const feeAmtBig: bigint = depAmtBig - lkAmtBig;

  // Safe conversion to number for SDK (which expects number for UInt fields).
  // For amounts that exceed Number.MAX_SAFE_INTEGER, flag an error rather than
  // silently passing a rounded value — this matches the SDK contract.
  const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);
  const depAmt: number = depAmtBig <= MAX_SAFE ? Number(depAmtBig) : NaN;
  const lkAmt: number = lkAmtBig <= MAX_SAFE ? Number(lkAmtBig) : NaN;
  const feeAmt: number = feeAmtBig <= MAX_SAFE ? Number(feeAmtBig) : NaN;

  // tokenBalances values are already converted to human-readable amounts in useTokenBalances
  // (raw base units divided by 10^decimals). Guard against undefined with ?? 0.
  const humanBalance = tokenBalances[tokenMint] ?? 0;

  // Native SOL balance for fee check — always available from tokenBalances regardless of
  // which token is selected, since useTokenBalances fetches all tokens in parallel.
  const solBalance = tokenBalances[SOL] ?? 0;
  // Only gate on SOL balance when the user is connected (i.e. we have their address).
  // When not connected, user can't submit anyway so the gate is irrelevant.
  const hasSolForFees = !user || solBalance >= MIN_SOL_FOR_FEES;

  const commitment = getCommitmentLevel(days);
  // Display-only estimated unlock timestamp. The authoritative value is computed
  // inside handleCreate at submit time so it always matches ts (Time.Now).
  const displayUnlockTs = Math.floor(Date.now() / 1000) + days * 86400;

  // Minimum raw units per token (small dust threshold)
  const minRawUnitsRef = (tokenMint === BONK || tokenMint === MEW) ? 100n : (tokenMint === USDC) ? 100n : 1000n;
  const isValid =
    amountNum > 0 &&
    depAmtBig >= minRawUnitsRef &&
    !isNaN(depAmt) && !isNaN(lkAmt) && // amounts must fit in Number.MAX_SAFE_INTEGER
    amountNum <= humanBalance &&
    days >= 1 &&
    days <= 1825;

  // Reset amount when token changes
  const handleTokenChange = (mint: string) => {
    setTokenMint(mint);
    setAmountStr('');
  };

  const handleCreate = useCallback(async (type: 'standard' | 'perma') => {
    if (!user || !isValid) return;
    setLoading(true);
    try {
      // Guard: amounts must be valid numbers (would be NaN only if > MAX_SAFE_INTEGER,
      // which isValid already prevents — this is a final safety check).
      if (isNaN(depAmt) || isNaN(lkAmt)) {
        throw new RangeError(
          `Amount exceeds safe integer range. depAmt=${depAmtBig}, lkAmt=${lkAmtBig}`,
        );
      }

      // Compute unlockAt at submit time (not render time) so it is anchored to
      // the actual moment of the write, not whenever the slider was last moved.
      // This keeps unlockAt consistent with ts (Time.Now, also set at write time)
      // and avoids scoring drift from (unlockAt - ts) being slightly off.
      const unlockTs = Math.floor(Date.now() / 1000) + days * 86400;

      const lockId = `${user.address}_${Date.now()}`;
      const lockData = {
        owner: Address.publicKey(user.address),
        depAmt,
        lkAmt,
        unlockAt: unlockTs,
        status: 'active',
        ts: Time.Now,
        lockType: type,
        tokenMint,
      };

      // Call the underlying `set` directly (bypassing setLocks's catch-all wrapper) so that
      // real thrown errors (network, RangeError, TypeError, etc.) propagate to the outer
      // catch below and show an accurate "Unexpected error" toast.
      // A falsy return value means the SDK received the call but the policy denied it.
      const succeeded = await set(`locks/${lockId}`, transformValues(lockData));
      const success = !!succeeded;

      if (success) {
        const amtDisplay = formatTokenAmount(lkAmtBig, tokenMint);
        const msg =
          type === 'perma'
            ? `${amtDisplay} sealed PERMANENTLY for ${formatDuration(days)}. No early exit.`
            : `${amtDisplay} sealed for ${formatDuration(days)}.`;
        toast.success(type === 'perma' ? 'PermaLOCK sealed!' : 'Lock created!', {
          description: msg,
        });
        setAmountStr('');
        setDays(30);
        setLockType('standard');
        setTokenMint(SOL);
        onClose();
      } else {
        // setLocks returned false — the SDK received the call but the server's policy
        // denied it. Log params to help diagnose the mismatch.
        console.warn('[CreateLockModal] setLocks denied by policy', {
          lockType: type,
          tokenMint,
          depAmt,
          lkAmt,
          unlockTs,
          humanBalance,
          amountNum,
        });
        toast.error('Lock denied by policy', {
          description:
            depAmtBig < 1n
              ? 'Amount too small — enter a valid amount.'
              : amountNum > humanBalance
              ? 'Insufficient balance for this lock.'
              : 'Transaction was rejected by the server. Check the browser console for details.',
        });
      }
    } catch (e) {
      // This branch catches real thrown errors (network failures, RangeError, TypeError, etc.)
      // — NOT policy denials, which are represented by setLocks returning false above.
      console.error('[CreateLockModal] unexpected error during lock creation', e);
      toast.error('Unexpected error', {
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [user, isValid, depAmtBig, lkAmtBig, depAmt, lkAmt, days, tokenMint, humanBalance, amountNum, onClose]);

  const content = (
    <div className="space-y-6 py-2">
      {/* Token selector */}
      <div>
        <label className="text-xs font-medium tracking-widest uppercase block mb-2" style={{ color: 'hsl(35 8% 50%)' }}>
          Token
        </label>
        <TokenSelector selectedMint={tokenMint} onChange={handleTokenChange} />
      </div>

      {/* Lock type toggle */}
      <div>
        <label className="text-xs font-medium tracking-widest uppercase block mb-2" style={{ color: 'hsl(35 8% 50%)' }}>
          Lock Type
        </label>
        <LockTypeToggle lockType={lockType} onChange={setLockType} />
      </div>

      {/* PermaLOCK warnings */}
      {lockType === 'perma' && (
        <div className="space-y-2">
          <div
            className="rounded-lg p-4 flex items-start gap-3"
            style={{
              background: 'hsl(45 80% 48% / 0.08)',
              border: '1px solid hsl(45 80% 48% / 0.35)',
            }}
          >
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" style={{ color: 'hsl(45 80% 58%)' }} />
            <p className="text-sm leading-relaxed" style={{ color: 'hsl(45 80% 75%)' }}>
              <strong>PermaLOCK</strong> means your {token.symbol} is locked until the timer expires. There is{' '}
              <strong>NO early unlock. No exceptions.</strong>
            </p>
          </div>
          <div
            className="rounded-lg p-4 flex items-start gap-3"
            style={{
              background: 'hsl(0 70% 45% / 0.1)',
              border: '1px solid hsl(0 70% 50% / 0.4)',
            }}
          >
            <Skull size={16} className="flex-shrink-0 mt-0.5" style={{ color: 'hsl(0 70% 62%)' }} />
            <p className="text-sm leading-relaxed" style={{ color: 'hsl(0 70% 75%)' }}>
              This action is <strong>IRREVERSIBLE.</strong> Your funds will be completely inaccessible
              until the unlock date.
            </p>
          </div>
        </div>
      )}

      {/* Amount */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium" style={{ color: 'hsl(40 15% 80%)' }}>
            Amount ({token.symbol})
          </label>
          {humanBalance > 0 && (
            <button
              onClick={() => {
                // Leave a small buffer for SOL to cover fees; for tokens use full balance
                const maxHuman = tokenMint === SOL
                  ? Math.max(0, humanBalance - 0.01)
                  : humanBalance;
                const decimals = token.decimals <= 5 ? 0 : 4;
                // Number.prototype.toFixed() switches to scientific notation for values >= 1e21,
                // which would crash the BigInt parser. Use toPrecision with a large precision and
                // then strip trailing zeros to produce a plain decimal string in all cases.
                // For the amounts users can hold (well below 1e21), toFixed is fine, but we guard
                // against it by checking first.
                let maxStr: string;
                if (!isFinite(maxHuman) || isNaN(maxHuman) || maxHuman <= 0) {
                  maxStr = '0';
                } else if (maxHuman >= 1e21) {
                  // Use toPrecision(21) which always returns plain decimal for non-insane values,
                  // then strip trailing zeros after the decimal point.
                  maxStr = maxHuman.toPrecision(21).replace(/\.?0+$/, '');
                } else {
                  maxStr = maxHuman.toFixed(decimals);
                }
                setAmountStr(maxStr);
              }}
              className="text-xs px-2 py-0.5 rounded transition-colors"
              style={{
                color: token.brandColorMuted,
                background: token.brandColorBg,
                border: `1px solid ${token.brandColorBorder}`,
              }}
            >
              MAX
            </button>
          )}
        </div>
        <div className="relative">
          <Input
            type="number"
            placeholder="0.00"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            className="pr-16 text-lg font-medium"
            style={{ background: 'hsl(30 5% 10%)', borderColor: 'hsl(35 12% 18%)' }}
            min="0"
            step={(tokenMint === BONK || tokenMint === MEW || tokenMint === ANGELIC_SOL || tokenMint === DRAGON) ? '1000' : (tokenMint === USDC) ? '0.01' : '0.01'}
          />
          <span
            className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold"
            style={{ color: token.brandColor }}
          >
            {token.symbol}
          </span>
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs" style={{ color: 'hsl(35 8% 52%)' }}>
            Balance: {
              token.decimals <= 5
                ? humanBalance.toLocaleString('en-US', { maximumFractionDigits: 0 })
                : humanBalance.toFixed(4)
            } {token.symbol}
          </span>
          {amountNum > humanBalance && humanBalance > 0 && (
            <span className="text-xs flex items-center gap-1" style={{ color: 'hsl(0 65% 60%)' }}>
              <AlertCircle size={10} /> Insufficient balance
            </span>
          )}
        </div>
      </div>

      {/* Fee breakdown */}
      {amountNum > 0 && (
        <div
          className="rounded-lg p-4 space-y-2 text-sm"
          style={{ background: 'hsl(30 5% 8%)', border: '1px solid hsl(35 12% 12%)' }}
        >
          <div className="flex justify-between">
            <span style={{ color: 'hsl(35 8% 52%)' }}>Deposit amount</span>
            <span>{formatTokenAmount(depAmtBig, tokenMint)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: 'hsl(35 8% 52%)' }}>1% deposit fee</span>
            <span style={{ color: 'hsl(0 65% 55%)' }}>-{formatTokenAmount(feeAmtBig, tokenMint)}</span>
          </div>
          <div
            className="flex justify-between pt-2 font-semibold"
            style={{ borderTop: '1px solid hsl(35 12% 14%)', color: token.brandColor }}
          >
            <span>Locked in vault</span>
            <span>{formatTokenAmount(lkAmtBig, tokenMint)}</span>
          </div>
        </div>
      )}

      {/* Duration */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium" style={{ color: 'hsl(40 15% 80%)' }}>
            Lock Duration
          </label>
          <div className="flex items-center gap-2">
            <span
              className="text-sm font-semibold"
              style={{ color: commitment.color, fontFamily: "'Cormorant Garamond', serif" }}
            >
              {commitment.label}
            </span>
            <span className="text-sm font-medium" style={{ color: 'hsl(40 15% 92%)' }}>
              {formatDuration(days)}
            </span>
          </div>
        </div>

        {/* Presets */}
        <div className="flex flex-wrap gap-2 mb-4">
          {PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              className="px-3 py-1 rounded text-xs font-medium transition-all duration-200"
              style={
                days === p.days
                  ? {
                      background: 'hsl(38 65% 57% / 0.2)',
                      border: '1px solid hsl(38 65% 57% / 0.5)',
                      color: 'hsl(43 75% 68%)',
                    }
                  : {
                      background: 'hsl(30 5% 10%)',
                      border: '1px solid hsl(35 12% 16%)',
                      color: 'hsl(35 8% 60%)',
                    }
              }
            >
              {p.label}
            </button>
          ))}
        </div>

        <Slider
          value={[days]}
          onValueChange={([v]) => setDays(v)}
          min={1}
          max={1825}
          step={1}
          className="w-full"
        />
        <div
          className="flex justify-between text-xs mt-1"
          style={{ color: 'hsl(35 8% 40%)' }}
        >
          <span>1 day</span>
          <span>5 years</span>
        </div>
      </div>

      {/* Summary */}
      {isValid && (
        <div
          className="rounded-lg p-4 space-y-2 text-sm"
          style={
            lockType === 'perma'
              ? {
                  background: 'hsl(0 65% 45% / 0.07)',
                  border: '1px solid hsl(0 65% 50% / 0.25)',
                }
              : {
                  background: 'hsl(38 65% 57% / 0.06)',
                  border: '1px solid hsl(38 65% 57% / 0.2)',
                }
          }
        >
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={14} style={{ color: lockType === 'perma' ? 'hsl(0 65% 60%)' : 'hsl(38 65% 57%)' }} />
            <span
              className="font-medium text-xs tracking-wide"
              style={{ color: lockType === 'perma' ? 'hsl(0 65% 60%)' : 'hsl(38 65% 57%)' }}
            >
              {lockType === 'perma' ? 'PERMALOCK SUMMARY' : 'LOCK SUMMARY'}
            </span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: 'hsl(35 8% 52%)' }}>Locked amount</span>
            <span className="font-medium">{formatTokenAmount(lkAmtBig, tokenMint)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: 'hsl(35 8% 52%)' }}>Duration</span>
            <span className="font-medium">{formatDuration(days)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: 'hsl(35 8% 52%)' }}>Unlocks on</span>
            <span className="font-medium">{formatUnlockDate(displayUnlockTs)}</span>
          </div>
          {lockType === 'standard' && (
            <div className="flex justify-between">
              <span style={{ color: 'hsl(35 8% 52%)' }}>Early unlock penalty</span>
              <span style={{ color: 'hsl(0 65% 55%)' }}>1%</span>
            </div>
          )}
          {lockType === 'perma' && (
            <div className="flex justify-between">
              <span style={{ color: 'hsl(35 8% 52%)' }}>Early unlock</span>
              <span style={{ color: 'hsl(0 70% 58%)', fontWeight: 600 }}>IMPOSSIBLE</span>
            </div>
          )}
        </div>
      )}

      {/* Low SOL warning — shown when connected wallet has insufficient SOL for fees */}
      {user && !hasSolForFees && (
        <div
          className="rounded-lg px-4 py-3 flex items-start gap-3 animate-fade-in"
          style={{
            background: 'hsl(38 80% 48% / 0.09)',
            border: '1px solid hsl(38 80% 48% / 0.38)',
          }}
        >
          <AlertCircle
            size={15}
            className="flex-shrink-0 mt-0.5"
            style={{ color: 'hsl(38 80% 62%)' }}
          />
          <p className="text-xs leading-relaxed" style={{ color: 'hsl(38 80% 72%)' }}>
            You need a little more SOL to cover network fees for this lock.
            Your wallet has <strong>{solBalance.toFixed(4)} SOL</strong> — a minimum of{' '}
            <strong>{MIN_SOL_FOR_FEES} SOL</strong> is required for gas and rent.
          </p>
        </div>
      )}

      {/* Action button */}
      {lockType === 'standard' ? (
        <Button
          className="w-full py-5 font-medium tracking-wide"
          disabled={!isValid || !hasSolForFees || loading}
          onClick={() => handleCreate('standard')}
          style={
            isValid
              ? {
                  background: 'linear-gradient(135deg, hsl(38 65% 52%), hsl(43 75% 60%))',
                  color: 'hsl(30 8% 4%)',
                  border: 'none',
                }
              : {}
          }
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <Zap size={14} className="animate-spin" /> Locking...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Lock size={14} /> Seal the Vault
            </span>
          )}
        </Button>
      ) : (
        <HoldButton
          disabled={!isValid || !hasSolForFees}
          onConfirm={() => handleCreate('perma')}
          loading={loading}
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="bottom" className="max-h-[90vh] flex flex-col px-6 pb-8">
          <SheetHeader className="mb-4 flex-shrink-0">
            <SheetTitle
              className="text-2xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Create a Lock
            </SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto flex-1 pr-1">
            {content}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col" style={{ background: 'hsl(30 6% 7%)', border: '1px solid hsl(35 12% 14%)' }}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle
            className="text-2xl"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Create a Lock
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 pr-1">
          {content}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateLockModal;
